/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/hooks/useProspectImport.js
 *
 * Description:
 * Custom hook for Prospect Import.
 *
 * Responsibilities
 * ----------------
 * - File Selection
 * - File Verification
 * - Import Execution
 * - Validation Summary
 * - Grid Records
 * - Search
 * - Status Filter
 * - Record Selection
 * - Progress State
 * - UI State Management
 *
 * This hook contains UI state only.
 * Business logic remains in the Service layer.
 * ============================================================================
 */

import { useEffect, useMemo, useState } from "react";

import campaignImportService from "../services/campaignImportService";

import {
    validateFile,
    canImport
} from "../utils/importValidation";

import {
    prepareGridRecords,
    selectSingleRecord,
    getSelectedRecord,
    searchRecords,
    filterByStatus,
    calculateSuccessPercentage
} from "../utils/importHelpers";

import {
    DEFAULT_SUMMARY
} from "../constants/campaignImportConstants";


const useProspectImport = () => {

    /**
     * ----------------------------------------------------------------------
     * States
     * ----------------------------------------------------------------------
     */

    const [selectedFile, setSelectedFile] = useState(null);

    const [summary, setSummary] = useState(DEFAULT_SUMMARY);

    const [records, setRecords] = useState([]);

    const [searchText, setSearchText] = useState("");

    const [statusFilter, setStatusFilter] = useState("ALL");

    const [selectedRecord, setSelectedRecord] = useState(null);

    const [verified, setVerified] = useState(false);

    const [loading, setLoading] = useState(false);

    const [importing, setImporting] = useState(false);

    const [progress, setProgress] = useState(0);

    const [message, setMessage] = useState("");

    const [showViewDrawer, setShowViewDrawer] = useState(false);
    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [showCompareDrawer, setShowCompareDrawer] = useState(false);

    /**
     * ----------------------------------------------------------------------
     * Derived Records
     * ----------------------------------------------------------------------
     */

    const filteredRecords = useMemo(() => {

        let data = filterByStatus(records, statusFilter);

        data = searchRecords(data, searchText);

        return data;

    }, [records, searchText, statusFilter]);

    /**
     * ----------------------------------------------------------------------
     * Progress %
     * ----------------------------------------------------------------------
     */

    const successPercentage = useMemo(() => {

        return calculateSuccessPercentage(summary);

    }, [summary]);

    /**
     * ----------------------------------------------------------------------
     * Select File
     * ----------------------------------------------------------------------
     */

    /**
 * ----------------------------------------------------------------------
 * Select File
 * ----------------------------------------------------------------------
 */

const handleFileChange = (event) => {

    const file = event.target?.files?.[0];

    if (!file) {

        return;

    }

    setSelectedFile(file);

    setVerified(false);

    setSummary(DEFAULT_SUMMARY);

    setRecords([]);

    setSelectedRecord(null);

    setProgress(0);

    setMessage("");

};

    /**
     * ----------------------------------------------------------------------
     * Verify File
     * ----------------------------------------------------------------------
     */

    const verifyFile = async () => {

        const validation = validateFile(selectedFile);

        if (!validation.valid) {

            setMessage(validation.message);

            return validation;

        }

        try {

            setLoading(true);

            setMessage("");

            const response =
                await campaignImportService.verifyFile(selectedFile);

            if (response.success) {

                setSummary(response.data.summary);

                setRecords(
                    prepareGridRecords(response.data.records)
                );

                setVerified(true);

                setProgress(100);

            }

            return response;

        } finally {

            setLoading(false);

        }

    };

    /**
     * ----------------------------------------------------------------------
     * Import Records
     * ----------------------------------------------------------------------
     */

    const importRecords = async () => {

        if (!canImport(summary)) {

            return;

        }

        try {

            setImporting(true);

            await campaignImportService.finalizeImport();

        } finally {

            setImporting(false);

        }

    };

    /**
     * ----------------------------------------------------------------------
     * Select Record
     * ----------------------------------------------------------------------
     */

    const selectRecord = (recordId) => {

        const updated =
            selectSingleRecord(records, recordId);

        setRecords(updated);

        setSelectedRecord(
            getSelectedRecord(updated)
        );

    };

    /**
     * ----------------------------------------------------------------------
     * Search
     * ----------------------------------------------------------------------
     */

    const updateSearch = (value) => {

        setSearchText(value);

    };

    /**
     * ----------------------------------------------------------------------
     * Filter
     * ----------------------------------------------------------------------
     */

    const updateStatusFilter = (value) => {

        setStatusFilter(value);

    };

    
    /**
     * ----------------------------------------------------------------------
     * Update Imported Prospect
     * ----------------------------------------------------------------------
     */

    const updateImportedProspect = (updatedRecord) => {

        console.log("Updated Prospect");

        console.log(updatedRecord);

        // Temporary implementation
        // Later this will call:
        // campaignImportService.updateImportedProspect()

        setShowEditDrawer(false);

    };
    
    /**
     * ----------------------------------------------------------------------
     * Cancel Import
     * ----------------------------------------------------------------------
     */

    const reset = () => {

        setSelectedFile(null);

        setSummary(DEFAULT_SUMMARY);

        setRecords([]);

        setVerified(false);

        setProgress(0);

        setSelectedRecord(null);

        setSearchText("");

        setStatusFilter("ALL");

        setMessage("");

    };

    /**
     * ----------------------------------------------------------------------
     * Initial Load
     * ----------------------------------------------------------------------
     */

    useEffect(() => {

        // Reserved for future initialization.

    }, []);

    /**
     * ----------------------------------------------------------------------
     * Button States
     * ----------------------------------------------------------------------
     */

    const canVerify =
        !!selectedFile && !loading;

    const canExecuteImport =
        verified &&
        canImport(summary) &&
        !importing;

    const hasSelection =
        selectedRecord !== null;

    
    /**
     * ----------------------------------------------------------------------
     * Compare
     * ----------------------------------------------------------------------
     */

    /**
 * ----------------------------------------------------------------------
 * Compare Drawer
 * ----------------------------------------------------------------------
 */

const compare = () => {

    if (!selectedRecord) {

        return;

    }

    setShowCompareDrawer(true);

};

const closeCompareDrawer = () => {

    setShowCompareDrawer(false);

};

    /**
     * ----------------------------------------------------------------------
     * Edit
     * ----------------------------------------------------------------------
     */

    /**
     * ----------------------------------------------------------------------
     * Edit Drawer
     * ----------------------------------------------------------------------
     */

    /**
     * ----------------------------------------------------------------------
     * Edit Drawer
     * ----------------------------------------------------------------------
     */

    const edit = () => {

        if (!selectedRecord) {

            return;

        }

        setShowEditDrawer(true);

    };

    const closeEditDrawer = () => {

        setShowEditDrawer(false);

    };


    /**
     * ----------------------------------------------------------------------
     * View
     * ----------------------------------------------------------------------
     */

    /**
     * ----------------------------------------------------------------------
     * View Drawer
     * ----------------------------------------------------------------------
     */

    const view = () => {

        if (!selectedRecord) {

            return;

        }

        setShowViewDrawer(true);

    };

    const closeViewDrawer = () => {

        setShowViewDrawer(false);

    };
    
    /**
     * ----------------------------------------------------------------------
     * Update Imported Prospect
     * ----------------------------------------------------------------------
     */

    
    /**
     * ----------------------------------------------------------------------
     * Return
     * ----------------------------------------------------------------------
     */

    return {

        /**
         * Data
         */
        selectedFile,
        summary,
        records: filteredRecords,
        selectedRecord,

        /**
         * UI
         */
        verified,
        loading,
        importing,
        progress,
        message,
        successPercentage,
        showViewDrawer,
        showEditDrawer,
        showCompareDrawer,

        /**
         * Search
         */
        searchText,
        statusFilter,

        /**
         * Buttons
         */
        canVerify,
        canExecuteImport,
        hasSelection,

        /**
         * Actions
         */
        handleFileChange,
        verifyFile,
        importRecords,
        selectRecord,
        updateSearch,
        updateStatusFilter,
        reset,

        compare,
        edit,
        view,

        closeCompareDrawer,
        closeViewDrawer,
        closeEditDrawer,

        updateImportedProspect
    };

};

export default useProspectImport;